import React from 'react';
import PropTypes from 'prop-types';
import { getTextFromMapping, getTextFromTableMapping } from '../../../utils/mappingUtils';
import { useColors } from '../../../hooks/useColors';
import Badge from './Badge';

/**
 * Component for rendering a badge with text from a mapping
 * @param {Object} props - Component props
 * @param {number|string} props.value - The value to map
 * @param {string} props.mappingString - Optional mapping string in format "id1=text1;id2=text2"
 * @param {string} props.tableName - Optional table name for table-based mapping
 * @param {string} props.fieldName - Optional field name for table-based mapping
 * @param {Object} props.colorMap - Map of values to color classes
 * @param {string} props.variant - Badge variant (default, primary, success, warning, danger)
 * @param {string} props.className - Additional CSS classes
 */
const MappedBadge = ({
  value,
  mappingString,
  tableName,
  fieldName,
  colorMap = {},
  variant = 'default',
  className = ''
}) => {
  const { semantic } = useColors();
  
  if (value === null || value === undefined) return null;
  
  // Get the text from mapping
  let text;
  if (tableName && fieldName) {
    text = getTextFromTableMapping(value, tableName, fieldName);
  } else if (mappingString) {
    text = getTextFromMapping(value, mappingString);
  } else {
    text = String(value);
  }
  
  // Get the color variant from colorMap if provided, otherwise use variant prop
  const intValue = parseInt(value);
  const badgeVariant = colorMap[intValue] ? undefined : variant;
  const customColor = colorMap[intValue];
  
  if (customColor) {
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${customColor} ${className}`}>
        {text}
      </span>
    );
  }
  
  return (
    <Badge variant={badgeVariant} className={className}>
      {text}
    </Badge>
  );
};

/**
 * Component for rendering a badge with text from a table field mapping
 */
export const TableMappedBadge = ({
  value,
  tableName,
  fieldName,
  colorMap = {},
  variant = 'default',
  className = ''
}) => {
  return (
    <MappedBadge
      value={value}
      tableName={tableName}
      fieldName={fieldName}
      colorMap={colorMap}
      variant={variant}
      className={className}
    />
  );
};

MappedBadge.propTypes = {
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  mappingString: PropTypes.string,
  tableName: PropTypes.string,
  fieldName: PropTypes.string,
  colorMap: PropTypes.object,
  variant: PropTypes.oneOf(['default', 'primary', 'success', 'warning', 'danger']),
  className: PropTypes.string
};

TableMappedBadge.propTypes = {
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  tableName: PropTypes.string.isRequired,
  fieldName: PropTypes.string.isRequired,
  colorMap: PropTypes.object,
  variant: PropTypes.oneOf(['default', 'primary', 'success', 'warning', 'danger']),
  className: PropTypes.string
};

export default MappedBadge;

